import type { Role } from '@/types';

/**
 * Roles allowed to approve or reject a request, mirroring the backend exactly.
 *
 * This is an **allowlist**, deliberately, not a denylist of the roles that may only read: the
 * three request controllers each admit Operational Associate at the class level (list + detail)
 * and then narrow approve/reject to `@Roles(ORDER_MANAGEMENT, SUPER_ADMIN, ADMIN)` —
 * `installation-request-admin.controller.ts:99,115`, `replacement-request-admin.controller.ts:136,161`,
 * `warranty-claim-admin.controller.ts:116,140`. Enumerating who is refused would silently admit a
 * sixth role the day one is added; enumerating who is allowed refuses it by default (ADO 119376).
 */
const REQUEST_REVIEWER_ROLES: Role[] = ['super_admin', 'admin'];

/**
 * May this viewer act on a request (Approve / Reject)?
 *
 * Read access is not gated here — an Operational Associate still sees every queue and every detail
 * drawer; only the two write actions are withheld, which is what makes their view read-only.
 *
 * `null` — an unrecognised wire role, which `roleSchema` degrades to rather than throwing
 * (ADR-0021) — is withheld everything: an unknown viewer is never privileged.
 */
export function canReviewRequest(viewerRole: Role | null): boolean {
  return viewerRole !== null && REQUEST_REVIEWER_ROLES.includes(viewerRole);
}
